import { useMemo } from "react";
import { Table, theme } from "antd";
import type { ColumnsType } from "antd/es/table";
import type {
  PlotDataTableProps,
  PlotDataTableRow,
  PlotTableColumn,
  PlotTableView,
} from "@/components/VivoPlots/types";
import { columnTitle } from "@/components/VivoPlots/functions/plotTableView";

const PREVIEW_TABLE_MAX_BODY_HEIGHT = 400;
const PREVIEW_TABLE_MIN_BODY_HEIGHT = 80;
const PREVIEW_TABLE_ROW_HEIGHT = 40;
const PREVIEW_TABLE_TEXT_COLUMN_WIDTH = 180;
const PREVIEW_TABLE_NUMERIC_COLUMN_WIDTH = 130;

const NUMERIC_COLUMN_ROLES = new Set<PlotTableColumn["role"]>([
  "count",
  "percentage",
  "y",
]);

const columnWidth = (column: PlotTableColumn) =>
  NUMERIC_COLUMN_ROLES.has(column.role)
    ? PREVIEW_TABLE_NUMERIC_COLUMN_WIDTH
    : PREVIEW_TABLE_TEXT_COLUMN_WIDTH;

const formatCellValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "number") {
    return Number.isInteger(value) ? `${value}` : value.toFixed(4);
  }

  return `${value}`;
};

const buildTableColumns = (
  columns: PlotTableColumn[],
  columnsTitleCreator?: (column: string) => string,
): ColumnsType<PlotDataTableRow> =>
  columns.map((column) => ({
    key: column.key,
    dataIndex: column.key,
    title: columnTitle(column, columnsTitleCreator),
    width: columnWidth(column),
    render: (value: unknown) => formatCellValue(value),
  }));

const getTableScrollWidth = (columns: PlotTableColumn[]) =>
  columns.reduce((total, column) => total + columnWidth(column), 0);

const buildTableDataSource = (
  columns: PlotTableColumn[],
  rows: PlotTableView["rows"],
): PlotDataTableRow[] => {
  const signatureOccurrences = new Map<string, number>();

  return rows.map((row) => {
    const signature = JSON.stringify(columns.map(({ key }) => [key, row[key]]));
    const occurrence = signatureOccurrences.get(signature) ?? 0;
    signatureOccurrences.set(signature, occurrence + 1);

    return {
      ...row,
      __rowKey: `${signature}:${occurrence}`,
    };
  });
};

const getTableBodyHeight = (rowCount: number) =>
  Math.min(
    PREVIEW_TABLE_MAX_BODY_HEIGHT,
    Math.max(
      PREVIEW_TABLE_MIN_BODY_HEIGHT,
      rowCount * PREVIEW_TABLE_ROW_HEIGHT,
    ),
  );

export default function PlotDataTable({
  columns,
  rows,
  columnsTitleCreator,
}: PlotDataTableProps) {
  const { token } = theme.useToken();
  const tableColumns = useMemo(
    () => buildTableColumns(columns, columnsTitleCreator),
    [columns, columnsTitleCreator],
  );
  const tableDataSource = useMemo(
    () => buildTableDataSource(columns, rows),
    [columns, rows],
  );
  const tableBodyHeight = useMemo(
    () => getTableBodyHeight(rows.length),
    [rows.length],
  );
  const tableScrollWidth = useMemo(
    () => getTableScrollWidth(columns),
    [columns],
  );
  const hasColumns = columns.length > 0;

  return (
    <div className="max-h-full w-full overflow-hidden rounded-lg bg-white p-4 shadow-omni">
      <Table<PlotDataTableRow>
        size="small"
        bordered
        columns={tableColumns}
        dataSource={tableDataSource}
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          size: "small",
        }}
        classNames={{ section: "plot-data-table-section" }}
        styles={{
          section: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          },
        }}
        rowKey="__rowKey"
        scroll={
          hasColumns ? { x: tableScrollWidth, y: tableBodyHeight } : undefined
        }
        virtual={hasColumns}
        locale={{
          emptyText: (
            <div className="py-8 text-center">
              Please define your plot configurations.
            </div>
          ),
        }}
      />
    </div>
  );
}
